import type sv from './sv'

type DeepString<T> = T extends string
  ? string
  : { readonly [K in keyof T]: DeepString<T[K]> }

const no: DeepString<typeof sv> = {
  title: 'Opprett din App',
  description:
    'Bygg appen din på Convex SaaS, utforsk dokumentasjonen og start reisen din.',
  nav: {
    dashboard: 'Oversikt',
    jobs: 'Stillinger',
    candidates: 'Kandidater',
    questionBank: 'Spørsmålsbank',
    templates: 'Maler',
    settings: 'Innstillinger',
    billing: 'Fakturering',
    documentation: 'Dokumentasjon',
    personalAccount: 'Personlig konto',
    topUpBalance: 'Fyll på saldo',
    theme: 'Tema',
    language: 'Språk',
    logOut: 'Logg ut',
  },
  header: {
    dashboard: {
      title: 'Oversikt',
      description: 'Administrer appene dine og se bruken din.',
    },
    jobs: {
      title: 'Stillinger',
      description: 'Administrer stillingsannonser og intervjumaler.',
    },
    settings: {
      title: 'Innstillinger',
      description: 'Administrer kontoinnstillingene dine.',
    },
    billing: {
      title: 'Fakturering',
      description: 'Administrer saldoen din og kjøp intervjuer.',
    },
    organisation: {
      title: 'Organisasjon',
      description: 'Administrer organisasjonsinnstillinger.',
    },
    team: {
      title: 'Team',
      description: 'Administrer teammedlemmer og tilganger.',
    },
    interviews: {
      title: 'Intervjurapport',
      description: 'Se kandidatens intervjuresultater og AI-analyse.',
    },
    questionBank: {
      title: 'Spørsmålsbank',
      description: 'Lagre og gjenbruk intervjuspørsmål på tvers av stillinger.',
    },
    templates: {
      title: 'Jobbmaler',
      description: 'Lagre og gjenbruk jobbkonfigurasjoner som maler.',
    },
    candidates: {
      title: 'Kandidater',
      description: 'Søk og filtrer kandidater på tvers av alle stillinger.',
    },
  },
  auth: {
    login: {
      title: 'Fortsett til Convex SaaS',
      subtitle: 'Velkommen tilbake! Logg inn for å fortsette.',
      emailLabel: 'E-post',
      emailPlaceholder: 'E-post',
      emailInvalid: 'E-postadressen er ikke gyldig.',
      continueWithEmail: 'Fortsett med e-post',
      termsText: 'Ved å klikke på fortsett godtar du våre',
      termsOfService: 'Vilkår for bruk',
      and: 'og',
      privacyPolicy: 'Personvernerklæring.',
      ssoSignIn: 'Logg inn med SSO',
      ssoOr: 'eller',
      ssoRedirecting: 'Omdirigerer til din identitetsleverandør…',
      ssoNotConfigured: 'SSO er ikke konfigurert for dette e-postdomenet.',
    },
    verify: {
      title: 'Sjekk innboksen din!',
      subtitle:
        'Vi har nettopp sendt et midlertidig passord til deg via e-post. Skriv det inn nedenfor.',
      codePlaceholder: 'Kode',
      codeLabel: 'Kode',
      codeMinLength: 'Koden må være minst 8 tegn.',
      continue: 'Fortsett',
      didNotReceive: 'Fikk du ikke koden?',
      requestNewCode: 'Be om ny kode',
      codeSent: 'En ny kode er sendt.',
      invalidCode: 'Ugyldig eller utløpt kode.',
    },
  },
  onboarding: {
    username: {
      title: 'Velkommen!',
      subtitle: 'La oss starte med å sette opp profilen din.',
      organizationNamePlaceholder: 'Organisasjonsnavn',
      organizationNameLabel: 'Organisasjonsnavn',
      firstNamePlaceholder: 'Fornavn',
      firstNameLabel: 'Fornavn',
      lastNamePlaceholder: 'Etternavn',
      lastNameLabel: 'Etternavn',
      usernamePlaceholder: 'Brukernavn',
      usernameLabel: 'Brukernavn',
      continue: 'Fortsett',
      hint: 'Du kan oppdatere brukernavnet ditt når som helst fra kontoinnstillingene.',
    },
  },
  jobs: {
    createJob: 'Opprett stilling',
    noJobs: 'Ingen stillinger ennå',
    noJobsDescription: 'Opprett din første stilling for å komme i gang.',
    statusDraft: 'Utkast',
    statusActive: 'Aktiv',
    statusPaused: 'Pauset',
    duplicate: 'Dupliser',
    pause: 'Pause',
    pauseConfirm: 'Er du sikker?',
    delete: 'Slett',
    deleteConfirm: 'Er du sikker?',
    form: {
      titleLabel: 'Tittel',
      titlePlaceholder: 'F.eks. Backend-utvikler',
      descriptionLabel: 'Beskrivelse',
      descriptionPlaceholder: 'Beskriv rollen og hva dere ser etter...',
      languageLabel: 'Intervjuspråk',
      languageSv: 'Svensk',
      languageEn: 'Engelsk',
      languageNo: 'Norsk',
      languageDa: 'Dansk',
      languageFi: 'Finsk',
      languageBoth: 'Svensk & Engelsk',
      allowedModalitiesLabel: 'Tillatte modaliteter',
      videoLabel: 'Video',
      videoRequired: 'Obligatorisk',
      videoOptional: 'Valgfri',
      videoDisabled: 'Deaktivert',
      audioLabel: 'Lyd',
      audioRequired: 'Obligatorisk',
      audioDisabled: 'Deaktivert',
      chatLabel: 'Chat',
      chatEnabled: 'Aktivert',
      chatDisabled: 'Deaktivert',
      expiryDaysLabel: 'Lenkens gyldighetsperiode (dager)',
      interviewCountLabel: 'Antall intervjuer',
      topNLabel: 'Antall toppkandidater å fremheve',
      save: 'Lagre',
      create: 'Opprett stilling',
      cancel: 'Avbryt',
    },
    tabs: {
      overview: 'Oversikt',
      questions: 'Spørsmål',
      screener: 'Screener',
      additionalInfo: 'Tilleggsinformasjon',
      modality: 'Modalitet',
      voice: 'Stemme',
      candidates: 'Kandidater',
      candidateActions: 'Handlinger',
      report: 'Rapport',
      settings: 'Innstillinger',
    },
    overview: {
      name: 'Navn',
      email: 'E-post',
      status: 'Status',
      score: 'Poeng',
      report: 'Rapport',
      noScore: '—',
      viewReport: 'Vis rapport',
      shortlist: 'Shortlist',
      shortlistedOnly: 'Bare shortlistede',
      shareShortlist: 'Del shortlist',
      copyLink: 'Kopier lenke',
      linkCopied: 'Lenke kopiert!',
    },
    shortlist: {
      title: 'Shortlistede kandidater',
      jobLabel: 'Stilling',
      name: 'Navn',
      status: 'Status',
      score: 'Poeng',
      noScore: '—',
      empty: 'Ingen kandidater er shortlistet ennå.',
    },
    launch: {
      button: 'Lanser stilling',
      topUpAndLaunch: 'Fyll på & lanser',
      active: 'Aktiv',
      insufficientBalance:
        'Du trenger minst {{required}} NOK for å lansere. Nåværende saldo: {{balance}} NOK',
    },
    preview: {
      button: 'Forhåndsvis intervju',
      banner: 'Dette er en forhåndsvisning — ingen kreditter trekkes.',
      loading: 'Oppretter forhåndsvisning...',
      error: 'Kunne ikke starte forhåndsvisning. Prøv igjen.',
      close: 'Lukk',
      completed: 'Forhåndsvisning avsluttet.',
      screener: {
        title: 'Forhåndsregistreringsspørsmål',
        continue: 'Fortsett',
      },
      additionalInfo: {
        title: 'Tilleggsinformasjon',
        required: '*',
        continue: 'Fortsett',
      },
      briefing: {
        aboutToStart: 'Du er i ferd med å starte et AI-ledet videointervju.',
        expectedDuration: 'Forventet varighet: omtrent {{minutes}} minutter.',
        quietEnvironment:
          'Sørg for at du befinner deg i et stille miljø med en fungerende mikrofon.',
        consentText: 'Jeg samtykker til å bli tatt opp under dette intervjuet.',
        termsOfService: 'Vilkår for bruk',
        privacyPolicy: 'Personvernerklæring',
        starting: 'Starter...',
        ready: 'Jeg er klar',
      },
      disqualified: {
        title: 'Takk for din interesse',
        body: 'Dessverre oppfyller du ikke kravene for denne stillingen.',
      },
      room: {
        connecting: 'Intervjueren kobler til...',
      },
    },
    voice: {
      loading: 'Laster stemmer...',
      noResults: 'Ingen stemmer samsvarer med filtrene dine.',
      filterLanguage: 'Språk',
      filterAccent: 'Aksent',
      filterCategory: 'Kategori',
      filterGender: 'Kjønn',
      filterAge: 'Alder',
    },
    screener: {
      questionPlaceholder: 'F.eks. Har du gyldig førerkort?',
      optionPlaceholder: 'Alternativ',
      singleSelect: 'Enkeltvalg',
      multiChoice: 'Flervalg',
      markDisqualifying: 'Merk som diskvalifiserende',
      disqualifyingHint:
        'Kryss av et alternativ for å diskvalifisere kandidater som velger det.',
      addOption: 'Legg til alternativ',
      removeOption: 'Fjern alternativ',
      addQuestion: 'Legg til spørsmål',
      remove: 'Fjern',
      save: 'Lagre screener',
      saving: 'Lagrer...',
      saved: 'Lagret',
      empty:
        'Ingen screenerspørsmål ennå. Legg til ditt første spørsmål nedenfor.',
      drag: 'Dra for å endre rekkefølge',
    },
    additionalInfo: {
      labelPlaceholder: 'F.eks. Portfolio-URL',
      placeholderPlaceholder: 'F.eks. https://...',
      typeText: 'Tekst',
      typeNumber: 'Tall',
      typeDate: 'Dato',
      typeUrl: 'URL',
      typeEmail: 'E-post',
      requiredLabel: 'Obligatorisk',
      addField: 'Legg til felt',
      remove: 'Fjern',
      save: 'Lagre felt',
      saving: 'Lagrer...',
      saved: 'Lagret',
      empty: 'Ingen felt ennå. Legg til ditt første felt nedenfor.',
      drag: 'Dra for å endre rekkefølge',
    },
    questions: {
      textPlaceholder: 'Skriv intervjuspørsmålet ditt...',
      competencyPlaceholder: 'Kompetanse (valgfritt)',
      followUpPlaceholder: 'Oppfølgingsspørsmål...',
      addQuestion: 'Legg til spørsmål',
      addFollowUp: 'Legg til oppfølging',
      showFollowUps: 'Oppfølgingsspørsmål ({{count}})',
      hideFollowUps: 'Skjul oppfølgingsspørsmål',
      remove: 'Fjern',
      removeFollowUp: 'Fjern oppfølging',
      saveToBank: 'Lagre til bank',
      addFromBank: 'Legg til fra bank',
      save: 'Lagre spørsmål',
      saving: 'Lagrer...',
      saved: 'Lagret',
      empty: 'Ingen spørsmål ennå. Legg til ditt første spørsmål nedenfor.',
      drag: 'Dra for å endre rekkefølge',
    },
    locked: {
      notice:
        'Dette kan ikke endres mens stillingen er aktiv. Pause stillingen for å gjøre endringer.',
    },
    generate: {
      title: 'Generer med AI',
      tabText: 'Lim inn tekst',
      tabUrl: 'URL',
      tabPdf: 'PDF',
      textPlaceholder: 'Lim inn en stillingsannonse her...',
      urlPlaceholder: 'https://example.com/stillingsannonse',
      pdfLabel: 'Velg PDF',
      pdfSelected: '{{name}}',
      generate: 'Generer',
      generating: 'Genererer...',
      addToJob: 'Legg til',
      discard: 'Forkast',
      addAll: 'Legg til alle',
      discardAll: 'Forkast alle',
      error: 'Kunne ikke generere spørsmål. Prøv igjen.',
    },
    settings: {
      reminderCadenceTitle: 'Påminnelsesplan',
      reminderCadenceDescription:
        'Angi hvilke dager etter invitasjon en påminnelse skal sendes. La stå tomt for standardplanen (halvveis, dagen før, siste dag).',
      reminderCadenceDefault:
        'Standardplan brukes (halvveis, dagen før, siste dag).',
      reminderDay: 'Dag {{day}}',
      addReminderDay: 'Legg til dag',
      removeReminderDay: 'Fjern påminnelsesdag',
      reminderDayPlaceholder: 'Dag (f.eks. 2)',
      competencyWeightsTitle: 'Kompetansevekter',
      competencyWeightsDescription:
        'Tildel en relativ vekt for hver kompetanse ved beregning av gjennomsnittlig poengsum. Standardvekt er 1 (lik vekting).',
      competencyWeightsNoCompetencies:
        'Ingen kompetanser definert ennå. Legg til kompetanser i intervjuspørsmålene dine først.',
      competencyWeightsLocked:
        'Vekter kan ikke endres mens stillingen er aktiv.',
      introMessageTitle: 'Introbeskrivelse',
      introMessageDescription:
        'Vises for kandidater på briefing-skjermen før intervjuet starter. La stå tomt for å bruke standardteksten.',
      introMessagePlaceholder: 'Legg til en personlig introduksjonsmelding…',
      outroMessageTitle: 'Outrobeskrivelse',
      outroMessageDescription:
        'Vises for kandidater på sluttskjermen etter at intervjuet er fullført. La stå tomt for å bruke standardteksten.',
      outroMessagePlaceholder: 'Legg til en personlig avslutningsmelding…',
      translationsTitle: 'Oversettelser',
      translationsDescription:
        'Oppgi jobbinnhold på alle intervjuspråk. Bruk oversettelsesknappen for å generere et utkast, gjennomgå og lagre det deretter.',
      candidateFeedbackTitle: 'Kandidattilbakemelding',
      candidateFeedbackDescription:
        'Vis kandidater deres personlige kompetansepoeng etter fullført intervju.',
      candidateFeedbackEnabled: 'Vis tilbakemelding til kandidater',
    },
    translations: {
      generating: 'Genererer…',
      translateFrom: 'Oversett fra {{language}}',
      draftHint:
        'AI-utkast — gjennomgå og rediger, klikk deretter utenfor for å lagre.',
      swedish: 'Svensk',
      english: 'Engelsk',
    },
  },
  questionBank: {
    addQuestion: 'Legg til spørsmål',
    textPlaceholder: 'Skriv intervjuspørsmålet ditt...',
    competencyPlaceholder: 'Kompetanse (valgfritt)',
    save: 'Lagre',
    empty: 'Ingen spørsmål ennå. Legg til ditt første spørsmål nedenfor.',
    delete: 'Slett',
    deleteConfirm: 'Er du sikker?',
  },
  interviews: {
    header: {
      title: 'Intervjurapport',
      description: 'Se kandidatens intervjuresultater og AI-analyse.',
    },
    candidates: {
      noInterviews: 'Ingen intervjuer ennå.',
      statusPending: 'Venter',
      statusInProgress: 'Pågår',
      statusCompleted: 'Fullført',
      statusFailed: 'Mislyktes',
      viewReport: 'Vis rapport',
      duration: '{{min}}m {{sec}}s',
      capacity: '{{used}} / {{total}} plasser brukt',
      increaseCapacity: 'Øk kapasitet',
      increaseCapacityTitle: 'Øk intervjukapasitet',
      increaseCapacityDescription:
        'Hver ekstra plass koster {{cost}} kreditter. Din nåværende saldo er {{balance}} kreditter.',
      additionalSlots: 'Ekstra plasser',
      increaseCapacityConfirm: 'Bekreft',
      increaseCapacityCancel: 'Avbryt',
      increaseCapacityInsufficientBalance:
        'Utilstrekkelig saldo. Du trenger {{required}} kreditter men har bare {{balance}}.',
    },
    candidateActions: {
      calendlyUrlLabel: 'Calendly-lenke',
      calendlyUrlDescription:
        'Lim inn Calendly-lenken din for å sende den til kandidater som går videre.',
      saveCalendlyUrl: 'Lagre',
      noCandidates: 'Ingen kandidater ennå.',
      statusInvited: 'Invitert',
      statusStarted: 'Påbegynt',
      statusCompleted: 'Fullført',
      statusExpired: 'Utløpt',
      statusDisqualified: 'Diskvalifisert',
      statusRejected: 'Avvist',
      statusAdvanced: 'Videresendt',
      reject: 'Avvis',
      advance: 'Send videre',
      reTake: 'Tillat nytt forsøk',
      rejectDialogTitle: 'Send avslagsmail',
      rejectDialogDescription:
        'Dette sender en avslagsmail til kandidaten. Du kan legge til en valgfri personlig melding nedenfor.',
      rejectCustomMessagePlaceholder: 'Valgfri personlig melding...',
      rejectConfirm: 'Send avslag',
      advanceConfirmTitle: 'Send kandidat videre',
      advanceConfirmDescription:
        'Dette sender en Calendly-lenke til kandidaten for å booke et intervju.',
      advanceConfirm: 'Send videre',
      reTakeConfirmTitle: 'Tillat nytt forsøk',
      reTakeConfirmDescription:
        'Dette tilbakestiller kandidatens invitasjonslenke og forlenger fristen. Kandidaten får en ny lenke.',
      reTakeConfirm: 'Tillat nytt forsøk',
      cancel: 'Avbryt',
      selected: '{{count}} valgt',
      bulkReject: 'Avvis valgte',
      bulkAdvance: 'Send valgte videre',
      bulkSendReminder: 'Send påminnelse',
      bulkRejectDialogTitle: 'Avvis valgte kandidater',
      bulkRejectDialogDescription:
        'Dette sender en avslagsmail til {{count}} kandidat(er). Du kan legge til en valgfri personlig melding nedenfor.',
      bulkRejectConfirm: 'Avvis alle',
    },
    report: {
      back: 'Tilbake til stilling',
      summary: 'Sammendrag',
      noSummary: 'Analyse pågår...',
      competencies: 'Kompetansevurdering',
      noScores: 'Ingen poeng ennå.',
      rationale: 'Begrunnelse',
      quotes: 'Sitater',
      noQuotes: 'Ingen sitater.',
      transcript: 'Transkripsjon',
      noTranscript: 'Ingen transkripsjon tilgjengelig.',
      duration: 'Varighet',
      completedAt: 'Fullført',
      shareLabel: 'Del rapport',
      shareEnabled: 'Deling aktivert',
      shareDisabled: 'Deling deaktivert',
      copyLink: 'Kopier lenke',
      linkCopied: 'Kopiert!',
      video: 'Videoopptak',
      noVideo: 'Ingen videoopptak tilgjengelig.',
      videoLoading: 'Laster video...',
      clips: 'Kompetanseklipp',
      noClips: 'Ingen klipp tilgjengelig ennå.',
      clipDuration: '{{sec}}s',
      reviewedBy: 'Gjennomgått av',
      noReviewers: 'Ingen har gjennomgått denne rapporten ennå.',
      notes: 'Notater',
      noNotes: 'Ingen notater ennå.',
      addNote: 'Legg til notat',
      notePlaceholder: 'Skriv et notat…',
      deleteNote: 'Slett',
      noteAdded: 'Notat lagt til.',
      aiDetection: 'AI-svarsdeteksjon',
      aiDetectionPending: 'Analyse pågår...',
      aiDetectionLow: 'Svar virker menneskelige',
      aiDetectionMedium: 'Noen AI-lignende mønstre funnet',
      aiDetectionHigh: 'Svar trolig AI-genererte',
      aiDetectionScore: 'Sannsynlighetspoeng',
      aiDetectionFlags: 'Flaggede segmenter',
    },
    chat: {
      title: 'Spør om kandidaten',
      placeholder: 'Still et spørsmål om kandidatens svar i intervjuet...',
      inputPlaceholder: 'Skriv et spørsmål...',
      send: 'Send',
      sending: 'Sender...',
      errorMessage: 'En feil oppstod. Prøv igjen.',
    },
    orgChat: {
      title: 'Spør om kandidater',
      placeholder:
        'Still et spørsmål om hvilken som helst kandidat i alle stillinger...',
      inputPlaceholder: 'F.eks. Hvem fikk høyest poeng i problemløsning?',
      send: 'Send',
      sending: 'Sender...',
      errorMessage: 'En feil oppstod. Prøv igjen.',
      toggle: 'AI-chat',
    },
    publicReport: {
      notFound: 'Denne rapporten er ikke lenger tilgjengelig.',
      notFoundDescription: 'Lenken kan ha utløpt eller deling er deaktivert.',
    },
    pdfExport: {
      downloadButton: 'Last ned',
      aggregatedReport: 'Last ned samlet rapport',
      transcripts: 'Last ned transkripter',
      individualReports: 'Last ned individuelle rapporter',
      drawerTitleTranscripts: 'Last ned transkripter',
      drawerTitleReports: 'Last ned individuelle rapporter',
      drawerDescription: 'Velg kandidater som skal inkluderes i nedlastingen.',
      selectAll: 'Velg alle',
      deselectAll: 'Fjern alle valg',
      downloadSelected: 'Last ned valgte',
      downloadAll: 'Last ned alle',
      generating: 'Genererer {{completed}} av {{total}}...',
      complete: 'Nedlasting klar',
      error: 'Eksport mislyktes. Prøv igjen.',
      noCompletedCandidates: 'Ingen gjennomførte intervjuer å eksportere.',
      cancel: 'Avbryt',
    },
    aggregateReport: {
      noCompletedInterviews: 'Ingen fullførte intervjuer ennå.',
      rank: 'Rangering',
      candidate: 'Kandidat',
      averageScore: 'Gjennomsnittspoeng',
      topN: 'Topp {{n}}',
      competencies: 'Kompetansefordeling',
    },
    candidateFeedback: {
      title: 'Dine resultater',
      preparing: 'Din personlige tilbakemelding forberedes…',
      competencies: 'Kompetansepoeng',
      scoreLabel: '{{score}} / 10',
    },
  },
  candidateSearch: {
    searchPlaceholder: 'Søk på navn eller e-post…',
    filterJob: 'Alle stillinger',
    filterStatus: 'Alle statuser',
    filterMinScore: 'Minimumspoeng',
    filterMaxScore: 'Maksimumspoeng',
    filterFromDate: 'Fra dato',
    filterToDate: 'Til dato',
    clearFilters: 'Fjern filtre',
    noResults: 'Ingen kandidater funnet.',
    columnName: 'Navn',
    columnJob: 'Stilling',
    columnStatus: 'Status',
    columnScore: 'Gjennomsnittspoeng',
    columnDate: 'Invitert',
    viewReport: 'Vis rapport',
    scoreValue: '{{score}}',
    noScore: '—',
  },
  notifications: {
    title: 'Varsler',
    markAllRead: 'Merk alle som lest',
    empty: 'Ingen varsler ennå.',
    typeInterviewCompleted: 'Intervju fullført',
    typeInterviewFailed: 'Intervju mislyktes',
    typeLowBalance: 'Lav saldo',
    typeJobActivated: 'Stilling aktivert',
    typeCandidateExpired: 'Kandidat utløpt',
  },
  dashboard: {
    getStarted: 'Kom i gang',
    getStartedDescription:
      'Utforsk dashbordet og kom i gang med din første app.',
    tipChangeLanguage: 'TIPS: Prøv å bytte språk!',
    exploreDocumentation: 'Utforsk dokumentasjonen',
    statsInvited: 'Inviterte',
    statsCompleted: 'Fullførte',
    statsAdvanced: 'Videresendte',
    statsAvgScore: 'Gjennomsnittlig poeng',
    analytics: {
      title: 'Analyse',
      jobTitle: 'Stilling',
      status: 'Status',
      completionRate: 'Fullføringsgrad',
    },
    accountLocked: {
      message:
        'Abonnementet ditt har utløpt. Aktiver på nytt for å fortsette å kjøre intervjuer.',
      reactivate: 'Aktiver på nytt',
    },
  },
  settings: {
    avatar: {
      title: 'Din avatar',
      description: 'Dette er din avatar. Den vises på profilen din.',
      hint: 'Klikk på avataren for å laste opp en egendefinert fra filene dine.',
      reset: 'Tilbakestill',
    },
    profile: {
      title: 'Din profil',
      description:
        'For- og etternavnet ditt vises for kandidater og kollegaer.',
      firstNamePlaceholder: 'Fornavn',
      lastNamePlaceholder: 'Etternavn',
      hint: 'Bruk ditt virkelige navn.',
      save: 'Lagre',
    },
    username: {
      title: 'Ditt brukernavn',
      description: 'Dette er brukernavnet ditt. Det vises på profilen din.',
      placeholder: 'Brukernavn',
      hint: 'Bruk maksimalt 32 tegn.',
      save: 'Lagre',
    },
    deleteAccount: {
      title: 'Slett konto',
      description:
        'Slett kontoen din permanent, alle prosjektene dine og alle relaterte data.',
      hint: 'Denne handlingen kan ikke angres, fortsett med forsiktighet.',
      confirm: 'Er du sikker?',
      delete: 'Slett konto',
    },
    contactSupport: {
      title: 'Kontakt support',
      description:
        'Rapporter en feil, still et spørsmål eller be om en funksjon.',
      topicLabel: 'Emne',
      topicBug: 'Feil',
      topicTechnicalIssue: 'Teknisk problem',
      topicBilling: 'Fakturering',
      topicGeneral: 'Generelt spørsmål',
      topicFeatureRequest: 'Funksjonsforespørsel',
      severityLabel: 'Alvorlighetsgrad',
      severityLow: 'Lav',
      severityMedium: 'Medium',
      severityHigh: 'Høy',
      severityCritical: 'Kritisk',
      subjectLabel: 'Emne',
      subjectPlaceholder: 'Kort beskrivelse av problemet',
      messageLabel: 'Melding',
      messagePlaceholder: 'Beskriv problemet ditt i detalj…',
      submit: 'Send',
      submitting: 'Sender…',
      successTitle: 'Forespørsel mottatt',
      successMessage: 'Billetten din er sendt. Referanse: {{ticketId}}',
      hint: 'Vi svarer så snart som mulig.',
    },
    tabs: {
      general: 'Generelt',
      billing: 'Fakturering',
      organisation: 'Organisasjon',
      branding: 'Merkevare',
      team: 'Team',
      notifications: 'Varsler',
    },
    branding: {
      title: 'Merkevare',
      description: 'Tilpass hvordan organisasjonen din vises for kandidater.',
      logoTitle: 'Logo',
      logoDescription: 'Vises på kandidatens intervjuinformasjonsskjerm.',
      uploadLogo: 'Last opp logo',
      uploading: 'Laster opp...',
      primaryColorTitle: 'Primærfarge',
      primaryColorDescription:
        'Brukes på knapper og aksenter i kandidatflyten.',
      websiteTitle: 'Nettside',
      websiteDescription:
        'Vises for kandidater som en lenke for å lære mer om selskapet ditt.',
      taglineTitle: 'Slagord',
      taglineDescription:
        'En kort beskrivelse av selskapet ditt som vises på informasjonsskjermen.',
      taglinePlaceholder: 'F.eks. Vi bygger fremtidens arbeid',
      hint: 'Disse innstillingene er synlige for kandidater.',
      save: 'Lagre',
      saving: 'Lagrer...',
      saved: 'Lagret',
    },
    notifications: {
      title: 'Varslingsinnstillinger',
      description: 'Velg hvilke e-postvarsler du vil motta.',
      lowBalanceLabel: 'Lav saldo',
      lowBalanceDescription:
        'Få en e-post når saldoen din synker til 5 eller færre intervjuer.',
      interviewCompletedLabel: 'Intervju fullført',
      interviewCompletedDescription:
        'Få en e-post når en kandidat fullfører intervjuet sitt.',
      reportReadyLabel: 'Rapport klar',
      reportReadyDescription:
        'Få en e-post når AI-analysen for et intervju er klar.',
      teamMemberJoinedLabel: 'Teammedlem ble med',
      teamMemberJoinedDescription:
        'Få en e-post når et nytt teammedlem blir med i organisasjonen din.',
    },
    team: {
      title: 'Teammedlemmer',
      description: 'Administrer teamets tilgang til organisasjonen.',
      inviteTitle: 'Inviter teammedlem',
      inviteDescription: 'Send en invitasjon via e-post.',
      emailLabel: 'E-post',
      emailPlaceholder: 'kollega@eksempel.no',
      roleLabel: 'Rolle',
      roleMember: 'Medlem',
      roleAdmin: 'Admin',
      roleOwner: 'Eier',
      invite: 'Inviter',
      inviteSent: 'Invitasjon sendt.',
      membersTitle: 'Nåværende medlemmer',
      remove: 'Fjern',
      removeDialogTitle: 'Fjern teammedlem',
      removeDialogDescription:
        'Er du sikker på at du vil fjerne {{name}} fra organisasjonen?',
      removeConfirm: 'Fjern',
      cancel: 'Avbryt',
      hint: 'Inviterte brukere får tilgang til organisasjonens ressurser.',
    },
    organisation: {
      title: 'Organisasjonsinnstillinger',
      description: 'Innstillinger som vises for kandidater.',
      displayNameTitle: 'Visningsnavn',
      displayNameDescription:
        'Dette navnet vises for kandidater i intervjuportalen.',
      displayNamePlaceholder: 'Organisasjonsnavn',
      termsUrlTitle: 'Lenke til vilkår for bruk',
      termsUrlDescription: 'Vises for kandidater ved intervjuinvitasjon.',
      termsUrlPlaceholder: 'https://example.com/terms',
      privacyUrlTitle: 'Lenke til personvernerklæring',
      privacyUrlDescription: 'Vises for kandidater ved intervjuinvitasjon.',
      privacyUrlPlaceholder: 'https://example.com/privacy',
      hint: 'Disse innstillingene vises for kandidater.',
      save: 'Lagre',
      sso: {
        title: 'Enkel pålogging (SSO)',
        description:
          'La teammedlemmer logge inn med organisasjonens identitetsleverandør.',
        emailDomainTitle: 'E-postdomene',
        emailDomainDescription:
          'E-postdomenet som brukes for å identifisere SSO-brukere (f.eks. selskap.no).',
        emailDomainPlaceholder: 'selskap.no',
        discoveryUrlTitle: 'OIDC Discovery-URL',
        discoveryUrlDescription:
          'URL-en for OpenID Connect-konfigurasjonen hos din identitetsleverandør.',
        discoveryUrlPlaceholder:
          'https://din-idp.example.com/.well-known/openid-configuration',
        clientIdTitle: 'Klient-ID',
        clientIdDescription:
          'Klient-ID-en som er registrert hos identitetsleverandøren.',
        clientIdPlaceholder: 'klient-id',
        clientSecretTitle: 'Klienthemmelighet',
        clientSecretDescription:
          'Klienthemmeligheten som er registrert hos identitetsleverandøren.',
        clientSecretPlaceholder: '••••••••',
        clientSecretSetOn: 'Satt den {{date}}',
        clientSecretRotate: 'Roter',
        clientSecretRemove: 'Fjern',
        saveConfig: 'Lagre SSO-konfigurasjon',
        enforceTitle: 'Krev SSO',
        enforceDescription:
          'Krev at teammedlemmer logger inn via SSO. Organisasjonseiere er alltid unntatt.',
        enforced: 'Aktivert',
        notEnforced: 'Ikke aktivert',
        hint: 'SSO-konfigurasjonen er kun synlig for organisasjonseiere.',
      },
    },
  },
  support: {
    contactForm: {
      nameLabel: 'Navn',
      namePlaceholder: 'Ditt navn',
      emailLabel: 'E-post',
      emailPlaceholder: 'din@epost.no',
      successTitle: 'Melding sendt',
      successMessage:
        'Vi har mottatt meldingen din og vil svare så snart som mulig.',
      errorGeneric: 'Noe gikk galt. Prøv igjen.',
    },
  },
  billing: {
    balanceTitle: 'Din saldo',
    balanceDescription: 'Tilgjengelig saldo for intervjuer.',
    subscriptionTitle: 'Abonnement',
    subscriptionDescription:
      'Start din 14-dagers gratis prøveperiode. Ingen avgift før prøveperioden er over.',
    subscriptionNote: 'Du omdirigeres til Polar for å fullføre registreringen.',
    subscriptionStatusActive: 'Aktiv',
    subscriptionStatusTrialing: 'Prøveperiode',
    subscribeLabel: 'Start gratis prøveperiode',
    manageSubscription: 'Administrer abonnement',
    trialEndsOn: 'Prøveperioden avsluttes {{date}}',
    topUpTitle: 'Fyll på saldo',
    topUpDescription: 'Velg et beløp for å fylle på kontoen din.',
    topUpNote: 'Du omdirigeres til Polar for betaling.',
    topUpLabel: 'Fyll på',
    invoiceHistoryTitle: 'Fakturahistorikk',
    invoiceHistoryDescription: 'Se dine tidligere kjøp og fakturaer.',
    redirectedToPolar: 'Du omdirigeres til Polar kundeportal.',
    viewInvoices: 'Vis fakturaer',
    billingEmailTitle: 'Fakturadresse',
    billingEmailDescription:
      'Kvitteringer og fakturaer fra Polar sendes til denne adressen.',
    billingEmailPlaceholder: 'faktura@eksempel.no',
    billingEmailSave: 'Lagre',
    billingEmailSaved: 'Lagret.',
    billingEmailHint:
      'La stå tomt for å bruke kontoens e-postadresse. Kun admins og eiere kan endre dette.',
    autoTopUpTitle: 'Automatisk påfylling',
    autoTopUpDescription:
      'Fyll automatisk på saldoen din når den faller under en terskel.',
    autoTopUpEnabled: 'Aktivert',
    autoTopUpDisabled: 'Deaktivert',
    autoTopUpNoPaymentMethod:
      'Ingen lagret betalingsmetode. Gjennomfør et kjøp først for å aktivere automatisk påfylling.',
    autoTopUpThresholdLabel: 'Fyll på når færre enn',
    autoTopUpThresholdUnit: 'intervjuer gjenstår',
    autoTopUpProductLabel: 'Påfyllingspakke',
    autoTopUpSave: 'Lagre',
    autoTopUpSaved: 'Lagret.',
  },
  checkout: {
    title: 'Betaling fullført',
    description: 'Betalingen din er gjennomført og saldoen din er oppdatert.',
    completed: 'Kjøpet fullført!',
    returnToDashboard: 'Gå tilbake til oversikten',
  },
  templates: {
    empty: 'Ingen maler ennå',
    emptyDescription: 'Lagre en eksisterende jobb som mal for å gjenbruke den.',
    questionCount: 'Spørsmål',
    screenerCount: 'Screener',
    interviewCount: 'Intervjuer',
    useTemplate: 'Bruk mal',
    saveAsTemplate: 'Lagre som mal',
    saveAsTemplateTitle: 'Lagre som mal',
    templateNameLabel: 'Malnavn',
    templateNamePlaceholder: 'F.eks. Senior Backend-utvikler',
    saveButton: 'Lagre',
    createFromTemplateTitle: 'Opprett stilling fra "{{name}}"',
    createJobButton: 'Opprett stilling',
  },
  candidate: {
    loading: 'Laster…',
    invalid: {
      title: 'Lenken er ugyldig',
      body: 'Denne invitasjonslenken har utløpt eller er ikke lenger gyldig.',
    },
    orgLocked: {
      title: 'Kontoen er inaktiv',
      body: 'Denne organisasjonens konto er for øyeblikket inaktiv. Kontakt rekruttereren for hjelp.',
    },
    disqualified: {
      title: 'Takk for din interesse',
      body: 'Dessverre oppfyller du ikke kravene for denne stillingen.',
    },
    screener: {
      title: 'Forhåndsregistreringsspørsmål',
      continue: 'Fortsett',
    },
    additionalInfo: {
      title: 'Tilleggsinformasjon',
      continue: 'Fortsett',
    },
    briefing: {
      howToConduct: 'Hvordan vil du gjennomføre intervjuet?',
      usesModality: 'Dette intervjuet bruker',
      aboutToStart: 'Du er i ferd med å starte et AI-ledet intervju.',
      expectedDuration: 'Forventet varighet: omtrent {{minutes}} minutter.',
      quietEnvironment:
        'Sørg for at du befinner deg i et stille miljø med en fungerende mikrofon.',
      aiNotice:
        'Svarene dine vurderes av et AI-system. En menneskelig rekrutterer gjennomgår alle AI-genererte resultater og tar hver ansettelsesavgjørelse selvstendig – ingen automatisert avgjørelse tas om søknaden din.',
      requestScores:
        'Du kan når som helst be om kompetansepoengene og begrunnelsene dine via e-post til',
      consentRecording:
        'Jeg samtykker til å bli tatt opp under dette intervjuet.',
      consentAi:
        'Jeg samtykker til at svarene mine behandles av AI under dette intervjuet.',
      termsOfService: 'Vilkår for bruk',
      privacyPolicy: 'Personvernerklæring',
      starting: 'Starter…',
      ready: 'Jeg er klar',
      insufficientBalance:
        'Intervjuet kunne ikke startes akkurat nå. Kontakt rekruttereren.',
      orgLockedError:
        'Denne organisasjonens konto er for øyeblikket inaktiv. Kontakt rekruttereren.',
    },
    modality: {
      video: { label: 'Video', description: 'Kamera og mikrofon' },
      audio: { label: 'Kun lyd', description: 'Mikrofon' },
      chat: { label: 'Tekst', description: 'Skriftlige svar' },
    },
    room: {
      connecting: 'Intervjueren kobler til…',
      questionOf: 'Spørsmål {{index}} av {{total}}',
      answered: '{{completed}} av {{total}} besvart',
      chatPlaceholder: 'Skriv svaret ditt…',
      chatSend: 'Send',
      chatLeave: 'Forlat',
    },
    completed: {
      title: 'Intervjuet er fullført',
      defaultOutro: 'Takk for din tid. Vi tar kontakt snart.',
      feedbackQuestion: 'Hvordan var opplevelsen din?',
      feedbackComment: 'Ytterligere kommentarer? (valgfritt)',
      feedbackSubmit: 'Send tilbakemelding',
      feedbackThanks: 'Takk for tilbakemeldingen.',
    },
    abandoned: {
      title: 'Intervjuet avsluttet',
      body: 'Du har forlatt intervjuet. Hvis det var tilsiktet, trenger du ikke gjøre noe mer.',
    },
    error: {
      title: 'Noe gikk galt',
      body: 'En uventet feil oppstod under intervjuet ditt. Prøv igjen eller kontakt support.',
      reportLink: 'Rapporter et teknisk problem',
      severityTitle: 'Hvor alvorlig var problemet?',
      severityLow: 'Mindre – jeg kunne fortsette',
      severityMedium: 'Betydelig – intervjuet ble forstyrret',
      severityHigh: 'Kritisk – jeg kunne ikke fullføre intervjuet',
      describeTitle: 'Beskriv hva som skjedde',
      describePlaceholder: 'F.eks. Lyden sluttet å fungere, siden frøs…',
      submit: 'Send rapport',
      submitting: 'Sender…',
      submitted: 'Rapporten din er sendt. Takk.',
    },
  },
  ai: {
    generate: 'Generer',
  },
}

export default no
